'use client'

import { createClient } from '@/shared/lib/supabase/client'
import { useUser } from '@/shared/hooks/useUser'
import { clearPagesCache } from '@/shared/lib/pwaCache'
import { track } from '@/shared/lib/analytics'
import { isStandaloneDisplay } from '@/shared/lib/pwaDisplay'

// Thin wrapper over the shared useUser() query so existing consumers keep the
// same { user, loading, signOut } shape. The actual fetch + auth subscription
// are deduped through React Query / QueryProvider, so N components calling
// useAuth() on one screen now share a single validated getUser().
export function useAuth() {
  const { data: user, isLoading: loading } = useUser()

  const signOut = async () => {
    // Capture before the auth call clears identity, so this lands on the
    // signed-in person. A `login_screen_viewed` NOT preceded by a `signed_out`
    // for the same user is a forced re-auth (the bug); an intentional sign-out
    // is the legitimate path.
    track('signed_out', { standalone: isStandaloneDisplay() })
    const supabase = createClient()
    await supabase.auth.signOut()
    // Drop the SW's cached pages before reloading. The service worker serves
    // navigations stale-while-revalidate (see public/sw.js), so a bare reload
    // would just repaint the cached *signed-in* shell and the middleware's
    // login rewrite would never show. Flushing first guarantees the reload
    // hits the network.
    try {
      await clearPagesCache()
    } catch {
      // Best-effort: the SW's own background revalidation likely refreshed the
      // cache to the login page anyway — reload regardless.
    }
    // Reload the current (in-scope) path rather than pushing to /login. In an
    // installed PWA, /login is outside the manifest scope, so navigating there
    // ejects the user into the browser; a full reload of where they already are
    // lets the middleware render the login UI in place — sign-out and the next
    // sign-in both stay inside the standalone window.
    window.location.reload()
  }

  return { user: user ?? null, loading, signOut }
}
